import React, { useState } from 'react';
import { Table, Tag, Result, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DecisionLog } from './types';
import { useGeoDecisionLogs } from './queries';
import { formatDateTime } from '../../utils/format';

export const DecisionLogsPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const { data, isLoading, isPlaceholderData, error, refetch } =
    useGeoDecisionLogs(page, perPage);

  if (error) {
    return (
      <Result
        status="error"
        title="Failed to load decision logs"
        subTitle={
          error instanceof Error ? error.message : 'An unexpected error occurred'
        }
        extra={
          <Button type="primary" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const columns: ColumnsType<DecisionLog> = [
    {
      title: 'Decision',
      dataIndex: 'blocked',
      key: 'blocked',
      render: (blocked: boolean) => (
        <Tag color={blocked ? 'red' : 'green'}>
          {blocked ? 'BLOCKED' : 'ALLOWED'}
        </Tag>
      ),
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      render: (ip: string | null) =>
        ip ? (
          <a
            href={`https://ipinfo.io/${encodeURIComponent(ip)}?lookup_source=search-bar`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <code className="bg-slate-100 px-1 py-0.5 rounded font-mono text-xs">
              {ip}
            </code>
          </a>
        ) : (
          '—'
        ),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      render: (country: string | null) =>
        country ? <Tag color="blue">{country}</Tag> : '—',
    },
    {
      title: 'Account ID',
      dataIndex: 'account_id',
      key: 'account_id',
      render: (accountId: number | null) => (accountId !== null ? accountId : '—'),
    },
    {
      title: 'Rule ID',
      dataIndex: 'rule_id',
      key: 'rule_id',
      render: (ruleId: number | null) => (ruleId !== null ? ruleId : '—'),
    },
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt: string) => formatDateTime(createdAt),
    },
  ];

  return (
    <div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.data || []}
        loading={isLoading}
        scroll={{ x: 700 }}
        pagination={{
          current: data?.meta?.page ?? page,
          pageSize: data?.meta?.per_page ?? perPage,
          total: data?.meta?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} decisions`,
          onChange: (newPage, newPageSize) => {
            if (newPageSize !== perPage) {
              setPage(1);
              setPerPage(newPageSize);
            } else {
              setPage(newPage);
            }
          },
          disabled: isPlaceholderData,
        }}
      />
    </div>
  );
};
